/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { Button, Form, Input, Select, Table, Tag, Tooltip } from 'antd';
import { FormComponentProps } from 'antd/es/form';
import moment from 'moment';
import { errorHandle } from '../../services';
import {
    DEFAULT_PAGE_SIZE,
    PAGE_SIZE_OPTIONS,
    PENDING_STATUS,
    PENDING_STATUS_OPTIONS,
} from './constants';
import { fetchPendingUnlistPage, mapPendingUnlistItem, pickPageResult } from './api';

const { Option } = Select;

const cleanParams = (params: Record<string, any>) => {
    const next = { ...params };
    Object.keys(next).forEach((key) => {
        if (next[key] === undefined || next[key] === null || next[key] === '') {
            delete next[key];
        }
    });
    return next;
};

const renderEllipsisCell = (text?: string) => {
    const value = text != null && String(text).trim() !== '' ? String(text).trim() : '-';
    if (value === '-') {
        return value;
    }
    return (
        <Tooltip title={value} placement="topLeft">
            <span className="shelf-rule__cell-ellipsis">{value}</span>
        </Tooltip>
    );
};

/** 剩余时间：进行中显示 expire_time - 当前时间；已执行显示「已到期」；其他状态显示「-」 */
const formatRemainTime = (record: any) => {
    const statusCode = record?.statusCode;
    const status = record?.status;
    const isExecuted = statusCode === PENDING_STATUS.EXECUTED || status === '已执行';
    if (isExecuted) {
        return '已到期';
    }
    const isRunning = statusCode === PENDING_STATUS.RUNNING || status === '进行中';
    if (!isRunning) {
        return '-';
    }
    const expireTime = record?.expireTimeRaw ?? record?.expireTime;
    if (
        expireTime === undefined ||
        expireTime === null ||
        expireTime === '' ||
        expireTime === '-'
    ) {
        return '-';
    }
    const expire = moment(expireTime);
    if (!expire.isValid()) {
        return '-';
    }
    const diffMs = Math.max(expire.diff(moment()), 0);
    const totalMinutes = Math.floor(diffMs / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}小时${minutes}分`;
};

const PendingTabCom = ({ form }: FormComponentProps) => {
    const [pageNum, setPageNum] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [searchParams, setSearchParams] = useState<Record<string, any>>({
        status: PENDING_STATUS.RUNNING,
    });
    const [listData, setListData] = useState<any[]>([]);

    const fetchList = (query: Record<string, any> = {}) => {
        const merged = {
            page: pageNum,
            per_page: pageSize,
            ...searchParams,
            ...query,
        };
        const payload = cleanParams(merged);
        setLoading(true);
        fetchPendingUnlistPage(payload)
            .then((res: any) => {
                const { list, total: nextTotal } = pickPageResult(res);
                setListData(
                    list.map((item: any, index: number) => mapPendingUnlistItem(item, index))
                );
                setTotal(nextTotal);
                setPageNum(payload.page || 1);
                setPageSize(payload.per_page || pageSize);
            })
            .catch(errorHandle)
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchList({ page: 1, per_page: pageSize });
    }, []);

    const handleSearch = (e?: React.FormEvent) => {
        if (e) {
            e.preventDefault();
        }
        form.validateFields((_err: any, values: any) => {
            const params = {
                keyword: values.keyword ? values.keyword.trim() : undefined,
                status: values.status || undefined,
            };
            setSearchParams(params);
            fetchList({ ...params, page: 1, per_page: pageSize });
        });
    };

    const handleReset = () => {
        form.resetFields();
        const params = { status: PENDING_STATUS.RUNNING };
        setSearchParams(params);
        fetchList({
            page: 1,
            per_page: pageSize,
            keyword: undefined,
            status: PENDING_STATUS.RUNNING,
        });
    };

    const columns: any[] = [
        {
            title: 'SKU',
            dataIndex: 'sku',
            key: 'sku',
            className: 'shelf-rule__ellipsis-col',
            render: (text: string) => renderEllipsisCell(text),
        },
        {
            title: 'SPU',
            dataIndex: 'spu',
            key: 'spu',
            className: 'shelf-rule__ellipsis-col',
            render: (text: string) => renderEllipsisCell(text),
        },
        {
            title: '商品名称',
            dataIndex: 'name',
            key: 'name',
            className: 'shelf-rule__ellipsis-col',
            render: (text: string) => renderEllipsisCell(text),
        },
        {
            title: '开始时间',
            dataIndex: 'startTime',
            key: 'startTime',
            className: 'shelf-rule__ellipsis-col',
            render: (text: string) => renderEllipsisCell(text),
        },
        {
            title: '到期时间',
            dataIndex: 'expireTime',
            key: 'expireTime',
            className: 'shelf-rule__ellipsis-col',
            render: (text: string) => renderEllipsisCell(text),
        },
        {
            title: '剩余时间',
            dataIndex: 'remain',
            key: 'remain',
            className: 'shelf-rule__ellipsis-col',
            render: (_text: string, record: any) => renderEllipsisCell(formatRemainTime(record)),
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            className: 'shelf-rule__ellipsis-col',
            render: (text: string) => <Tag color="blue">{text}</Tag>,
        },
        {
            title: '取消原因',
            dataIndex: 'cancelReason',
            key: 'cancelReason',
            className: 'shelf-rule__ellipsis-col',
            render: (text: string) => renderEllipsisCell(text),
        },
    ];

    return (
        <div>
            <Form layout="inline" onSubmit={handleSearch} className="shelf-rule__filter">
                <Form.Item label="SKU / SPU">
                    {form.getFieldDecorator('keyword')(
                        <Input placeholder="SKU、SPU或名称" style={{ width: 220 }} />
                    )}
                </Form.Item>
                <Form.Item label="状态">
                    {form.getFieldDecorator('status', {
                        initialValue: PENDING_STATUS.RUNNING,
                    })(
                        <Select allowClear placeholder="请选择" style={{ width: 140 }}>
                            {PENDING_STATUS_OPTIONS.map((item) => (
                                <Option key={item.value} value={item.value}>
                                    {item.label}
                                </Option>
                            ))}
                        </Select>
                    )}
                </Form.Item>
                <Form.Item>
                    <Button type="primary" htmlType="submit">
                        查询
                    </Button>
                    <Button style={{ marginLeft: 8 }} onClick={handleReset}>
                        重置
                    </Button>
                </Form.Item>
            </Form>
            <Table
                className="shelf-rule__table"
                rowKey="id"
                loading={loading}
                columns={columns}
                dataSource={listData}
                locale={{ emptyText: '暂无待下架记录' }}
                pagination={{
                    total,
                    current: pageNum,
                    pageSize,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    pageSizeOptions: PAGE_SIZE_OPTIONS,
                    showTotal: (count) => `共 ${count} 条记录`,
                    onChange: (page) => {
                        fetchList({ page, per_page: pageSize });
                    },
                    onShowSizeChange: (_current, size) => {
                        fetchList({ page: 1, per_page: size });
                    },
                }}
            />
        </div>
    );
};

export const PendingTab = Form.create()(PendingTabCom);
